import { useEffect, useRef, useState } from "react";
import api, { API_BASE } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PenLine, Eraser, FileText, Download } from "lucide-react";

/**
 * Signature pad + delivery capture dialog.
 * Props:
 *  - open (bool), onOpenChange(fn)
 *  - parcel (obj)
 *  - onDelivered() -- called after signature saved
 */
export default function DeliveryReceiptDialog({ open, onOpenChange, parcel, onDelivered }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [signedBy, setSignedBy] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [hasStroke, setHasStroke] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSignedBy(parcel?.recipient_name || "");
    setRemarks("");
    // Reset canvas after render
    setTimeout(() => clear(), 50);
  }, [open, parcel]);

  const point = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return {
      x: (t.clientX - rect.left) * (canvas.width / rect.width),
      y: (t.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = point(e);
    ctx.strokeStyle = "#0A0A0A";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasStroke(true);
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // baseline
    ctx.strokeStyle = "#DDD"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(20, canvas.height - 20); ctx.lineTo(canvas.width - 20, canvas.height - 20); ctx.stroke();
    setHasStroke(false);
  };

  const submit = async () => {
    if (!hasStroke) return toast.error("Please capture a signature");
    if (!signedBy) return toast.error("Signed-by name required");
    setBusy(true);
    try {
      const data = canvasRef.current.toDataURL("image/png");
      const { data: resp } = await api.post(`/parcels/${parcel.id}/deliver`, {
        signature_base64: data,
        signed_by: signedBy,
        delivered_by: "", // backend defaults to current user
        remarks,
      });
      toast.success("Delivered · signature captured");
      onDelivered && onDelivered(resp);
      // Trigger PDF download in a new tab (uses auth header via fetch + blob)
      const pdfResp = await fetch(`${API_BASE}/parcels/${parcel.id}/receipt`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("mr_token")}` },
      });
      const blob = await pdfResp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `receipt-${parcel.tracking_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl flex items-center gap-2">
            <PenLine size={22} className="text-primary" /> Delivery handoff
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4" data-testid="delivery-receipt-dialog">
          <div className="grid grid-cols-2 gap-4 text-sm border border-border p-3">
            <div>
              <div className="overline">Tracking</div>
              <div className="mono">{parcel?.tracking_number}</div>
            </div>
            <div>
              <div className="overline">Recipient</div>
              <div className="font-semibold">{parcel?.recipient_name}</div>
            </div>
          </div>

          <div>
            <div className="overline mb-1">Recipient signature (tablet-friendly)</div>
            <div className="border-2 border-foreground bg-white">
              <canvas
                ref={canvasRef}
                width={720}
                height={200}
                data-testid="signature-canvas"
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                style={{ touchAction: "none", cursor: "crosshair", width: "100%", height: 180 }}
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="text-[11px] mono text-muted-foreground">Sign inside the box with a stylus or finger.</div>
              <Button type="button" data-testid="signature-clear" onClick={clear} variant="outline" size="sm" className="rounded-md">
                <Eraser size={14} className="mr-2" /> Clear
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="overline">Signed by *</span>
              <Input data-testid="signed-by" value={signedBy} onChange={(e)=>setSignedBy(e.target.value)}
                className="mt-1 rounded-md border-input" required />
            </label>
            <label className="block">
              <span className="overline">Remarks</span>
              <Input data-testid="delivery-remarks" value={remarks} onChange={(e)=>setRemarks(e.target.value)}
                className="mt-1 rounded-md border-input" placeholder="e.g. Handed at Finance desk" />
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button type="button" variant="outline" onClick={()=>onOpenChange(false)} className="rounded-md">Cancel</Button>
            <Button
              type="button"
              data-testid="delivery-submit"
              onClick={submit} disabled={busy}
              className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut"
            >
              <FileText size={14} className="mr-2" /> {busy ? "Saving…" : "Save & Download PDF"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Standalone download-only button for parcels that are already delivered */
export function DownloadReceiptButton({ parcel, testid = "receipt-download" }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const resp = await fetch(`${API_BASE}/parcels/${parcel.id}/receipt`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("mr_token")}` },
      });
      if (!resp.ok) throw new Error("Not available");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `receipt-${parcel.tracking_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Receipt not available yet");
    } finally { setBusy(false); }
  };
  return (
    <Button type="button" data-testid={testid} onClick={download} disabled={busy}
      variant="outline" className="rounded-md border-foreground text-xs">
      <Download size={14} className="mr-2" /> {busy ? "…" : "Receipt PDF"}
    </Button>
  );
}
